import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Box,
  Typography,
  Alert,
  Checkbox,
  Chip,
} from "@mui/material";
import moment from "moment";

const ConflictResolutionDialog = ({
  open,
  onClose,
  onConfirm,
  conflictData,
  isLoading = false,
}) => {
  const [selectedOption, setSelectedOption] = useState("overlap");
  const [notifyDoctor, setNotifyDoctor] = useState(false);

  if (!conflictData) return null;

  const { conflictingAppointments, movingAppointment } = conflictData;
  const conflict = conflictingAppointments[0]; // Por agora assumimos 1 conflito

  const canSwap =
    conflictingAppointments.length === 1 &&
    (movingAppointment?.canSwap || false);

  const isToday = moment().isSame(moment(conflict.start), "day");
  const showNotifyCheckbox = isToday && selectedOption === "overlap";

  const handleConfirm = () => {
    onConfirm({
      action: selectedOption,
      notifyDoctor: selectedOption === "overlap" ? notifyDoctor : false,
      conflictingAppointmentId: conflict.id,
    });
  };

  const formatTime = (date) => {
    return moment(date).format("HH:mm");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Conflito de Horário Detectado
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Já existe um agendamento no horário selecionado
        </Alert>

        {/* Informação do conflito */}
        {/* Informação do(s) conflito(s) */}
        {conflictingAppointments.length > 1 ? (
          <Box sx={{ maxHeight: 300, overflow: "auto", mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {conflictingAppointments.length} conflitos detectados
            </Alert>
            {conflictingAppointments.map((conflict, index) => (
              <Box
                key={conflict.id}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "#FFF3F6",
                  borderRadius: 1,
                  borderLeft: "3px solid #D96C60",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Conflito {index + 1}:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {conflict.cliente} - {conflict.procedimento}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatTime(conflict.start)} - {formatTime(conflict.end)} (
                  {conflict.duration}min)
                  <Chip label={conflict.model} size="small" sx={{ ml: 1 }} />
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ mb: 3, p: 2, bgcolor: "#FFF3F6", borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Agendamento existente:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {conflict.cliente} - {conflict.procedimento}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTime(conflict.start)} - {formatTime(conflict.end)} (
              {conflict.duration}min)
              <Chip label={conflict.model} size="small" sx={{ ml: 1 }} />
            </Typography>
          </Box>
        )}

        {/* Opções de resolução */}
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
          >
            {/* Opção 1: Encaixar */}
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
                mb: 2,
                bgcolor:
                  selectedOption === "overlap" ? "#f5f5f5" : "transparent",
              }}
            >
              <FormControlLabel
                value="overlap"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Encaixar mesmo assim
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Os dois agendamentos ficarão sobrepostos na agenda
                    </Typography>
                  </Box>
                }
              />

              {/* Checkbox de notificação */}
              {selectedOption === "overlap" && showNotifyCheckbox && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notifyDoctor}
                        onChange={(e) => setNotifyDoctor(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Notificar médico sobre o encaixe (agendamento para hoje)
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>

            {/* Opção 2: Trocar (condicional) */}
            {canSwap && (
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  bgcolor:
                    selectedOption === "swap" ? "#f5f5f5" : "transparent",
                }}
              >
                <FormControlLabel
                  value="swap"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Trocar agendamentos entre médicos
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {conflict.cliente} será movido para o médico atual
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* Mensagem quando não pode trocar */}
            {!canSwap && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Troca não disponível:{" "}
                {Math.abs(conflict.duration - movingAppointment.duration) > 15
                  ? `durações incompatíveis (${movingAppointment.duration}min vs ${conflict.duration}min)`
                  : "agendamento conflitante já foi atendido ou marcado como falta"}
              </Alert>
            )}
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? "Processando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConflictResolutionDialog;
